<template>
  <div class="login-page">
    <div class="login-page__shell">
      <header class="login-page__head">
        <p class="login-page__kicker">계정</p>
        <h1 class="login-page__title">로그인</h1>
      </header>

      <form class="login-page__panel login-form" @submit.prevent="onSubmit">
        <div class="login-form__body">
          <p v-if="justJoined" class="login-form__welcome">
            회원가입이 완료되었습니다. 아이디와 비밀번호를 채워 두었습니다. 로그인만 하시면 됩니다.
          </p>
          <div v-if="showAdminSession" class="login-form__session login-form__session--admin">
            <p>
              <strong>{{ existingAdmin?.name || existingAdmin?.userId }}</strong>
              관리자로 이미 들어가 있습니다.
            </p>
            <div class="login-form__session-actions">
              <button type="button" class="login-form__submit" @click="goAdminHome">
                관리 화면 입장
              </button>
              <button type="button" class="login-form__ghost" @click="forceMemberLogin = true">
                다른 계정으로 로그인
              </button>
            </div>
          </div>
          <div v-if="showSellerSession" class="login-form__session">
            <p>
              <strong>{{ existingSeller.storeName }}</strong>
              판매업체로 이미 로그인되어 있습니다.
            </p>
            <div class="login-form__session-actions">
              <button type="button" class="login-form__submit" @click="redirectAfterSellerLogin">
                판매관리센터 입장
              </button>
              <button type="button" class="login-form__ghost" @click="onSellerRelogin">
                다른 계정으로 로그인
              </button>
            </div>
          </div>

          <template v-if="showLoginFields">
            <PublicLoginKindChoice
              v-if="needsKindChoice"
              :kinds="pendingKinds || []"
              :busy="isSubmitting"
              @pick="onPickKind"
              @reset="onResetKind"
            />
            <template v-else>
              <div class="login-form__social">
                <KakaoLoginButton :busy="kakaoBusy" @click="onKakaoLogin" />
                <p class="login-form__split">또는 아이디로 로그인</p>
              </div>
              <label class="login-form__field">
                <span>아이디</span>
                <input
                  v-model="form.userId"
                  type="text"
                  autocomplete="username"
                  placeholder="아이디를 입력해 주세요"
                  maxlength="40"
                  :class="userIdInputState"
                  @input="onUserIdInput"
                />
              </label>

              <label class="login-form__field">
                <span>비밀번호</span>
                <input
                  v-model="form.password"
                  type="password"
                  autocomplete="current-password"
                  placeholder="비밀번호를 입력해 주세요"
                  :class="passwordInputState"
                />
              </label>

              <label class="login-form__auto">
                <input v-model="rememberMe" type="checkbox" />
                <span>자동 로그인</span>
                <em>공용 PC에서는 사용하지 마세요</em>
              </label>
            </template>
          </template>
        </div>

        <div v-if="showLoginFields && !needsKindChoice" class="login-form__actions">
          <button type="submit" class="login-form__submit" :disabled="isSubmitting">
            {{ isSubmitting ? '로그인 중...' : '로그인' }}
          </button>
          <router-link to="/signup" class="login-form__ghost login-form__signup">
            회원가입
          </router-link>
        </div>

        <div v-if="showLoginFields && !needsKindChoice" class="login-form__links">
          <router-link to="/find-id" class="login-form__link">아이디 찾기</router-link>
          <span class="login-form__divider" aria-hidden="true" />
          <router-link to="/find-password" class="login-form__link">비밀번호 찾기</router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import KakaoLoginButton from '@/components/auth/KakaoLoginButton.vue'
import PublicLoginKindChoice from '@/components/auth/PublicLoginKindChoice.vue'
import { startKakaoLogin } from '@/composables/useKakaoOAuth'
import { applyMemberHomeAfterLogin, memberHomeAfterLogin } from '@/composables/useLoginDestination'
import { consumeLoginPrefill } from '@/constants/authPrefill'
import { loadAutoLogin } from '@/constants/autoLogin'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { useSellerAuth } from '@/composables/useSellerAuth'
import {
  decidePublicLoginKind,
  identifyPublicLogin,
  preferAdminLogin,
  preferSellerLogin,
  customerPathAfterStaffLogin,
  sellerHomePath,
  type PublicLoginKind,
} from '@/composables/useUnifiedLogin'
import {
  inputValidationState,
  isLoginPasswordValid,
  isLoginUserIdValid,
  sanitizeLoginUserIdInput,
} from '@/utils/formValidation'

const route = useRoute()
const router = useRouter()
const { login: loginMember, isLoggedIn, initMemberAuth } = useMemberAuth()
const { admin, isAdminLoggedIn, initAdminAuth, login: loginAdmin } = useAdminAuth()
const {
  login: loginSeller,
  logout: logoutSeller,
  seller,
  isSellerLoggedIn,
  initSellerAuth,
} = useSellerAuth()

const form = reactive({
  userId: '',
  password: '',
})

const isSubmitting = ref(false)
const kakaoBusy = ref(false)
const rememberMe = ref(loadAutoLogin('member') || loadAutoLogin('seller') || loadAutoLogin('admin'))
const forceSellerRelogin = ref(false)
const forceMemberLogin = ref(false)
const pendingKinds = ref<PublicLoginKind[] | null>(null)

const justJoined = computed(() => route.query.joined === '1')
const preferSeller = computed(() => preferSellerLogin(route.query))
const preferAdmin = computed(() => preferAdminLogin(route.query))
const existingSeller = computed(() => (isSellerLoggedIn.value ? seller.value : null))
const existingAdmin = computed(() => (isAdminLoggedIn.value ? admin.value : null))
const showAdminSession = computed(() => {
  if (!existingAdmin.value || forceMemberLogin.value) return false
  const next = redirectPath.value
  /* 몰·고객 화면으로 가려다 온 경우에는 관리 화면 입장을 띄우지 않는다. */
  if (next && !next.startsWith('/admin')) return false
  return true
})
const showSellerSession = computed(
  () => Boolean(existingSeller.value) && preferSeller.value && !forceSellerRelogin.value,
)
const showLoginFields = computed(() => !showAdminSession.value && !showSellerSession.value)
const needsKindChoice = computed(() => Boolean(pendingKinds.value))
const onUserIdInput = (event: Event) => {
  form.userId = sanitizeLoginUserIdInput((event.target as HTMLInputElement).value)
  pendingKinds.value = null
}

const userIdInputState = computed(() =>
  inputValidationState(form.userId, isLoginUserIdValid(form.userId)),
)

const passwordInputState = computed(() =>
  inputValidationState(form.password, isLoginPasswordValid(form.password)),
)

const onSellerRelogin = async () => {
  forceSellerRelogin.value = true
  form.userId = ''
  form.password = ''
  pendingKinds.value = null
  try {
    await logoutSeller()
  } catch {
    /* 세션이 이미 끝났어도 입력 폼은 연다 */
  }
}

const redirectPath = computed(() =>
  typeof route.query.redirect === 'string' ? route.query.redirect : '',
)

const goAdminHome = async () => {
  const next = redirectPath.value
  const stay =
    next.startsWith('/admin') && !next.startsWith('/admin/login') ? next : '/admin'
  await router.push(stay)
}

const redirectAfterMemberLogin = async () => {
  await router.push(redirectPath.value || applyMemberHomeAfterLogin())
}

const redirectAfterSellerLogin = async () => {
  await router.push(sellerHomePath(seller.value, redirectPath.value))
}

const redirectAfterStaffLogin = async () => {
  await router.push(customerPathAfterStaffLogin(redirectPath.value))
}

const onKakaoLogin = async () => {
  if (kakaoBusy.value || isSubmitting.value) {
    return
  }
  kakaoBusy.value = true
  try {
    await startKakaoLogin({
      redirect: redirectPath.value || memberHomeAfterLogin(),
      remember: rememberMe.value,
    })
  } catch (error) {
    kakaoBusy.value = false
    window.alert(error instanceof Error ? error.message : '카카오 로그인을 시작하지 못했습니다.')
  }
}

async function enterAs(kind: PublicLoginKind) {
  if (kind === 'seller') {
    await loginSeller(form.userId, form.password, rememberMe.value)
    await redirectAfterStaffLogin()
    return
  }
  if (kind === 'admin') {
    await loginAdmin(form.userId, form.password, rememberMe.value)
    await redirectAfterStaffLogin()
    return
  }
  await loginMember(form.userId, form.password, rememberMe.value)
  await redirectAfterMemberLogin()
}

const onPickKind = async (kind: PublicLoginKind) => {
  if (isSubmitting.value) {
    return
  }
  isSubmitting.value = true
  try {
    await enterAs(kind)
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '로그인에 실패했습니다.')
  } finally {
    isSubmitting.value = false
  }
}

const onResetKind = () => {
  pendingKinds.value = null
}

const onSubmit = async () => {
  if (isSubmitting.value || needsKindChoice.value) {
    return
  }

  if (!isLoginUserIdValid(form.userId)) {
    window.alert('아이디 형식을 확인해 주세요.')
    return
  }

  if (!isLoginPasswordValid(form.password)) {
    window.alert('비밀번호를 입력해 주세요.')
    return
  }

  isSubmitting.value = true

  try {
    const kinds = await identifyPublicLogin(form.userId, form.password)
    const decision = decidePublicLoginKind(kinds, preferSeller.value, preferAdmin.value)
    if (decision === 'choose') {
      pendingKinds.value = kinds
      return
    }
    await enterAs(decision)
  } catch (error) {
    const message = error instanceof Error ? error.message : '로그인에 실패했습니다.'
    window.alert(message)
  } finally {
    isSubmitting.value = false
  }
}

onMounted(async () => {
  const shouldCheckSeller =
    preferSeller.value || Boolean(seller.value) || loadAutoLogin('seller')

  await Promise.all([
    initMemberAuth(),
    initAdminAuth(),
    shouldCheckSeller || redirectPath.value.startsWith('/mall')
      ? initSellerAuth()
      : Promise.resolve(),
  ])

  if (
    redirectPath.value.startsWith('/mall') &&
    !redirectPath.value.startsWith('/mall/signup') &&
    (isAdminLoggedIn.value || isSellerLoggedIn.value)
  ) {
    await router.replace(redirectPath.value)
    return
  }

  if (preferSeller.value) {
    const prefill = consumeLoginPrefill()
    if (prefill) {
      form.userId = prefill.userId
      form.password = prefill.password
    }
    return
  }

  if (isLoggedIn.value && !preferAdmin.value) {
    await redirectAfterMemberLogin()
    return
  }

  const prefill = consumeLoginPrefill()
  if (!prefill) {
    return
  }

  form.userId = prefill.userId
  form.password = prefill.password
})
</script>

<style lang="scss" src="./LoginPage.scss"></style>
